import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AuthModal from './AuthModal';
import usageTracker from '../services/usageTracker';

const { FiTarget, FiUser, FiLogIn, FiMenu, FiX, FiStar, FiZap, FiSettings, FiLogOut } = FiIcons;

const Header = ({ userProfile, onUserUpdate }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin'); // 'signin' or 'signup'
  const [user, setUser] = useState(null);
  const [userUsage, setUserUsage] = useState(null);
  const [remainingCalculations, setRemainingCalculations] = useState(3);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);

  // Load user data and usage stats
  useEffect(() => {
    loadUserData();
    updateUsageStats();
  }, []);

  const loadUserData = () => {
    const userData = localStorage.getItem('meal_calculator_user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  };

  const updateUsageStats = async () => {
    setIsLoadingUsage(true);
    try {
      const usage = await usageTracker.getCurrentUserUsage();
      setUserUsage(usage);
      const remaining = await usageTracker.getRemainingFreeCalculations();
      setRemainingCalculations(remaining);
    } catch (error) {
      console.error('Error loading usage stats:', error);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  const handleSignIn = (mode = 'signin') => {
    setAuthMode(mode);
    setShowAuthModal(true);
    setShowMobileMenu(false);
  };

  const handleSignUp = () => {
    setAuthMode('signup');
    setShowAuthModal(true);
    setShowMobileMenu(false);
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    localStorage.setItem('meal_calculator_user', JSON.stringify(userData));
    setShowAuthModal(false);
    updateUsageStats();
    if (onUserUpdate) {
      onUserUpdate(userData);
    }
  };

  const handleSignOut = () => {
    setUser(null);
    localStorage.removeItem('meal_calculator_user');
    setShowMobileMenu(false);
    // Reset usage stats for guest user
    updateUsageStats();
    if (onUserUpdate) {
      onUserUpdate(null);
    }
  };

  const getUserDisplayName = () => {
    if (!user) return '';
    return user.firstName || user.name || user.email?.split('@')[0] || 'User';
  };

  const getCreditsDisplay = () => {
    if (isLoadingUsage) return '...';
    if (userUsage?.hasPaid) return 'Unlimited';
    return `${remainingCalculations} left`;
  };

  const getCreditsColor = () => {
    if (userUsage?.hasPaid) return 'text-green-600';
    if (remainingCalculations <= 1) return 'text-red-600';
    if (remainingCalculations <= 2) return 'text-orange-600';
    return 'text-blue-600';
  };

  return (
    <>
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiTarget} className="text-2xl text-indigo-600" />
              <span className="text-xl font-bold text-gray-800">CreateMyMeal</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <>
                  {/* User Info */}
                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiUser} className="text-gray-600" />
                      <span className="font-medium text-gray-800">{getUserDisplayName()}</span>
                    </div>
                    <div className="h-4 w-px bg-gray-300"></div>
                    <div className="flex items-center gap-1">
                      <SafeIcon 
                        icon={userUsage?.hasPaid ? FiStar : FiZap} 
                        className={`text-sm ${getCreditsColor()}`} 
                      />
                      <span className={`text-sm font-semibold ${getCreditsColor()}`}>
                        {getCreditsDisplay()}
                      </span>
                    </div>
                  </div>

                  {/* Settings Menu */}
                  <div className="relative group">
                    <button className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                      <SafeIcon icon={FiSettings} className="text-lg" />
                    </button>
                    
                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="py-1">
                        <button
                          onClick={handleSignOut}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        >
                          <SafeIcon icon={FiLogOut} className="text-gray-500" />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Sign In Link */}
                  <button
                    onClick={() => handleSignIn('signin')}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
                  >
                    <SafeIcon icon={FiLogIn} className="text-lg" />
                    <span>Sign In</span>
                  </button>

                  {/* Sign Up Button */}
                  <button
                    onClick={() => handleSignUp()}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <SafeIcon icon={showMobileMenu ? FiX : FiMenu} className="text-xl" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {showMobileMenu && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-200"
            >
              <div className="px-4 py-4 space-y-3">
                {user ? (
                  <>
                    {/* User Info Mobile */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiUser} className="text-gray-600" />
                        <span className="font-medium text-gray-800">{getUserDisplayName()}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <SafeIcon 
                          icon={userUsage?.hasPaid ? FiStar : FiZap} 
                          className={`text-sm ${getCreditsColor()}`} 
                        />
                        <span className={`text-sm font-semibold ${getCreditsColor()}`}>
                          {getCreditsDisplay()}
                        </span>
                      </div>
                    </div>

                    {/* Sign Out Button Mobile */}
                    <button
                      onClick={handleSignOut}
                      className="w-full flex items-center gap-2 p-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiLogOut} className="text-gray-500" />
                      <span>Sign Out</span>
                    </button>
                  </>
                ) : (
                  <>
                    {/* Sign In Mobile */}
                    <button
                      onClick={() => handleSignIn('signin')}
                      className="w-full flex items-center gap-2 p-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiLogIn} className="text-lg" />
                      <span>Sign In</span>
                    </button>

                    {/* Sign Up Mobile */}
                    <button
                      onClick={() => handleSignUp()}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-lg font-medium transition-colors"
                    >
                      Sign Up
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onSuccess={handleAuthSuccess}
        onModeChange={setAuthMode}
      />
    </>
  );
};

export default Header;